<template>
  <div>
    <div id="navbar-menu" :class="`${navbarInteraction ? navbarCollapsed ? 'navbar-bg-deactivated' : 'navbar-bg-activated' : ''}`">
      <div class="navbar-brand">
        <a class="navbar-logo-link" @click="closeNavbar('bookings')">
          <img class="logo" src="/logo.png" alt="X-Cube Hub" />
        </a>
        <div class="navbar-brand-meta">
          <span v-if="isTestEnvironment" class="test-environment-text">Test Env</span>
          <span class="navbar-version">{{ appVersion }}</span>
        </div>
      </div>

      <div id="navbar-content-container" :class="`${navbarCollapsed ? 'navbar-hide' : 'navbar-show'}`">
        <template v-for="item in links">
          <span
            v-if="item.type === 'link'"
            :key="item.route"
            :class="`${item.route == selectedRoute ? 'selected-nav-link' : ''} ${!navbarCollapsed ? 'navbar-item-show-border' : ''} navbar-item`"
          >
            <a @click="closeNavbar(item.route)" class="navbar-item-content">{{ item.label }}</a>
          </span>
          <span
            v-else
            :key="item.id"
            :class="`${groupContainsRoute(item, selectedRoute) ? 'selected-nav-link' : ''} ${!navbarCollapsed ? 'navbar-item-show-border' : ''} navbar-item navbar-group`"
          >
            <el-dropdown class="navbar-group-desktop" trigger="click" @command="closeNavbar">
              <span class="navbar-item-content navbar-dropdown-trigger">
                {{ item.label }}<i class="el-icon-arrow-down el-icon--right" />
              </span>
              <el-dropdown-menu slot="dropdown" class="hub-nav-dropdown">
                <el-dropdown-item
                  v-for="child in item.children"
                  :key="child.route"
                  :command="child.route"
                  :class="{ 'is-active-route': child.route === selectedRoute }"
                >
                  {{ child.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <div class="navbar-group-mobile">
              <a
                class="navbar-item-content navbar-dropdown-trigger"
                @click.stop="toggleGroup(item.id)"
              >
                {{ item.label }}
                <i
                  class="el-icon-arrow-down el-icon--right"
                  :class="{ 'is-open': openGroupId === item.id }"
                />
              </a>
              <div v-show="openGroupId === item.id" class="navbar-group-children">
                <a
                  v-for="child in item.children"
                  :key="child.route"
                  class="navbar-item-content"
                  :class="{ 'selected-child': child.route === selectedRoute }"
                  @click="closeNavbar(child.route)"
                >{{ child.label }}</a>
              </div>
            </div>
          </span>
        </template>

        <span style="flex-grow: 1;"></span>
        <span :class="`${!navbarCollapsed ? 'navbar-item-show-border' : ''} navbar-item navbar-user`">
          <span class="navbar-username">{{ userName }}</span>
          <a @click="handleLogout" class="navbar-item-content">Logout</a>
        </span>
      </div>

      <div id="navbar-hamburger-button-container" :class="`${navbarCollapsed ? 'navbar-align-justify-icon' : 'navbar-justify-icon'}`">
        <span v-if="navbarCollapsed" @click="toggleNavbarVisibility" class="navbar-icon-wrapper">
          <font-awesome-icon icon="fa fa-bars" class="resize-navbar-icon"/>
        </span>
        <span v-else @click="toggleNavbarVisibility" class="navbar-icon-wrapper">
          <font-awesome-icon icon="fa fa-xmark" class="resize-navbar-icon"/>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { version } from "../../../package.json";
import { isTestEnvironment as determineIsTestEnvironment } from "../../helpers/auth";
import { groupContainsRoute, menuForRole, roleFromUser } from "../../helpers/menuLinks";
import useCurrentUserStore from "../../store/userStore";
import { logout } from "../../api/rest";

const store = useCurrentUserStore();
const navbarCollapsed = ref(true);
const navbarInteraction = ref(false);
const openGroupId = ref(null);

const router = useRouter();
const route = useRoute();

const isTestEnvironment = computed(() => determineIsTestEnvironment());

const links = computed(() => menuForRole(roleFromUser(store.currentUser)));

const userName = computed(() => store.currentUser?.username || "unknown user");

const appVersion = computed(() => version);

const selectedRoute = computed(() => route.name);
const toggleNavbarVisibility = () => {
    navbarInteraction.value = true;
    const willOpen = navbarCollapsed.value;
    navbarCollapsed.value = !navbarCollapsed.value;
    if (willOpen) {
        const currentGroup = links.value.find((item) => (
            item.type === "group" && groupContainsRoute(item, selectedRoute.value)
        ));
        openGroupId.value = currentGroup ? currentGroup.id : null;
    } else {
        openGroupId.value = null;
    }
};
const toggleGroup = (id) => {
    openGroupId.value = openGroupId.value === id ? null : id;
};
const closeNavbar = (routeName) => {
    navbarCollapsed.value = true;
    openGroupId.value = null;
    if (route.name !== routeName) {
        router.push({ name: routeName });
    }
};
const handleLogout = () => {
    logout();
    router.push("/login");
};
</script>

<style scoped lang="scss">
@use "../../colors" as *;
@use "../../style/page-shell" as shell;

.resize-navbar-icon{
  height: 1.5rem;
}

.selected-nav-link{
  border-bottom: 1.5px solid $color-primary;
}

a:hover{
  cursor:pointer;
}

.navbar-brand {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 0 0 auto;
  min-width: 0;
}

.navbar-logo-link {
  display: flex;
  align-items: center;
  line-height: 0;
}

.logo {
  display: block;
  height: 40px;
  width: 40px;
}

.navbar-brand-meta {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.1rem;
  line-height: 1.2;
  font-size: 0.75rem;
  font-weight: 600;
  color: $color-text-primary;
}

.test-environment-text {
  color: $color-primary;
}

#navbar-menu{
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  display: flex;
  align-items: flex-end;
  gap: 2.5rem;
  width: 100%;
  height: shell.$hub-nav-height;
  padding: 0.55rem shell.$hub-page-gutter 0.75rem;
  background-color: transparent;
  border-bottom: 1px solid rgba($color-text-regular, 0.45);
}

#navbar-content-container{
  display: flex;
  align-items: flex-end;
  width: 100%;
  min-width: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
}

#navbar-hamburger-button-container{
  display: none;
}

.navbar-item {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
  color: $color-primary;
}

.navbar-user {
  flex-direction: row;
  align-items: baseline;
  gap: 0.25rem;
}

.navbar-username {
  color: $color-text-primary;
  white-space: nowrap;
  padding: 0 0 0 12px;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}

.navbar-item-content, .navbar-item-content:hover{
  color: $color-text-regular;
  white-space: nowrap;
  padding: 0 16px;
  cursor: pointer;
  caret-color: transparent;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  font-family: inherit;
}

.navbar-item :deep(.el-dropdown) {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  font-family: inherit;
  color: inherit;
}

.navbar-dropdown-trigger {
  display: inline-flex;
  align-items: center;
}

.navbar-group-mobile {
  display: none;
}

.navbar-icon-wrapper:hover{
  cursor:pointer;
}

@media screen and (max-width: 768px) {
  #navbar-menu {
    padding-left: shell.$hub-page-gutter-mobile;
    padding-right: shell.$hub-page-gutter-mobile;
  }
}

@media screen and (max-width:992px){
  .navbar-icon-wrapper{
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 0.15rem;
  }

  #navbar-menu{
    &.navbar-bg-activated {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "brand close"
        "links links";
      align-items: center;
      align-content: start;
      gap: 0.75rem 1rem;
      height: 100%;
      overflow-y: auto;
      background-color: $color-background;
      border-bottom: 0;
      animation-name: showNavbar;
      animation-duration: 0.5s;
    }

    &.navbar-bg-activated > .navbar-brand {
      grid-area: brand;
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: $color-background;
    }

    &.navbar-bg-activated > #navbar-hamburger-button-container {
      grid-area: close;
      margin-left: 0;
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: $color-background;
    }

    &.navbar-bg-activated > #navbar-content-container{
      grid-area: links;
      animation-name: fadeIn;
      animation-duration: 2s;
    }

    &.navbar-bg-deactivated{
      animation-name: removeNavbar;
      animation-duration: 0.5s;
    }
  }

  #navbar-content-container{
    flex-direction: column;
    align-items: stretch;
    flex: 1 1 auto;
    &.navbar-hide{
      visibility: hidden;
    }
    &.navbar-show{
      justify-content: flex-start;
    }
  }

  #navbar-hamburger-button-container {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex: 0 0 auto;
    margin-left: auto;
  }

  .navbar-item{
    align-items: flex-start;
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
    &.navbar-item-show-border{
      border-bottom: 1px solid $color-text-regular;
    }
  }

  .navbar-user {
    flex-direction: column;
    align-items: flex-start;
  }

  .navbar-username {
    padding: 0 16px 0.35rem;
  }

  .navbar-group-desktop {
    display: none;
  }

  .navbar-group-mobile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .navbar-group-children {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    padding: 0.65rem 0 0.15rem;
  }

  .navbar-group-children .navbar-item-content {
    padding-top: 0.55rem;
    padding-bottom: 0.55rem;
    padding-left: 2rem;
  }

  .navbar-group-children .selected-child {
    color: $color-primary;
  }

  .navbar-dropdown-trigger .is-open {
    transform: rotate(180deg);
  }
}

/**Start keyframe animations */
@keyframes showNavbar{
  from {
    height:0%;
  }
  to {
    height:100%;
  }
}

@keyframes removeNavbar{
  from {
    height: 100%;
    background-color: $color-background;
  }
  to {
    height: 0%;
    background-color: $color-background;
  }
}

@keyframes fadeIn{
  from {
    opacity:0;
  }
  to {
    opacity: 1;
  }
}

</style>

<style lang="scss">
@use "../../colors" as *;

.hub-nav-dropdown {
  background-color: $color-background;
  border: 1px solid $color-primary;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;

  .el-dropdown-menu__item {
    font-size: inherit;
    font-weight: inherit;
    color: $color-text-regular;

    &:hover,
    &:focus {
      background-color: rgba($color-primary, 0.12);
      color: $color-primary;
    }

    &.is-active-route {
      color: $color-primary;
    }
  }
}
</style>
